import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { toast } from 'react-toastify';
import { BASE_URL, API_BASE_URL, institute_id } from '../config/config';

/**
 * AI Flashcard Generator Page
 * Creates flashcard sets from study materials for spaced repetition learning
 */
const AIFlashcardGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage } = useSubscription();
  
  const [topic, setTopic] = useState('');
  const [content, setContent] = useState('');
  const [numCards, setNumCards] = useState(10);
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await checkFeatureAccess('flashcard_generator');
    if (!access.has_access) {
      toast.error(access.message || 'You need to upgrade to use this feature');
      navigate('/subscription/plans');
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim() && !content.trim()) {
      toast.error('Please enter a topic or paste study content');
      return;
    }

    setLoading(true);
    
    try {
      console.log('Generating flashcards with:', {
        topic,
        content: content.substring(0, 100),
        num_cards: numCards,
        user_id: user?.id
      });

      const response = await fetch(`${API_BASE_URL}/ai-subscription/features/flashcards/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Institute-ID': user?.institute_id || '',
        },
        body: JSON.stringify({
          topic: topic,
          content: content,
          num_cards: numCards,
          user_id: user?.id
        })
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        console.log('Flashcards generated:', data.data.flashcards);
        setFlashcards(data.data.flashcards);
        setCurrentCardIndex(0);
        setIsFlipped(false);
        
        await logFeatureUsage('flashcard_generator', {
          topic: topic || 'from content',
          num_cards: data.data.flashcards.length
        });
        
        toast.success(`Generated ${data.data.flashcards.length} flashcards!`);
      } else {
        console.error('Generation failed:', data.message);
        toast.error(data.message || 'Failed to generate flashcards');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate flashcards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentCardIndex < flashcards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleDownload = () => {
    const text = flashcards.map((card, idx) => 
      `Card ${idx + 1}:\nQ: ${card.front}\nA: ${card.back}\n\n`
    ).join('');
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flashcards-${topic || 'study-set'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Flashcards downloaded!');
  };

  const currentCard = flashcards[currentCardIndex];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ai/features')}
            className="text-purple-600 hover:text-purple-700 mb-4 flex items-center gap-2"
          >
            ← Back to AI Features
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            🎴 AI Flashcard Generator
          </h1>
          <p className="text-gray-600">
            Create flashcard sets from any topic or study material for effective learning
          </p>
        </div>

        {flashcards.length === 0 ? (
          // Generation Form
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                ⚙️ Generate Flashcards
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., Photosynthesis, World War 2, JavaScript Basics"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <div className="text-center text-gray-500 text-sm">OR</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paste Your Study Content
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Paste your notes, textbook content, or study material here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[200px] resize-y"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Flashcards: {numCards}
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="30"
                    value={numCards}
                    onChange={(e) => setNumCards(parseInt(e.target.value))}
                    className="w-full"
                    disabled={loading}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>30</span>
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading || (!topic.trim() && !content.trim())}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Generating...
                    </>
                  ) : (
                    <>
                      ✨ Generate Flashcards
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Tips Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3">
                💡 Tips for Best Results
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Enter a specific topic for focused flashcards</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Or paste content to generate cards from your notes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Start with 10-15 cards for optimal learning</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Use flashcards for spaced repetition review</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Test yourself regularly for better retention</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          // Flashcard Display
          <div>
            {/* Card Navigation */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-600">
                Card {currentCardIndex + 1} of {flashcards.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setFlashcards([]);
                    setTopic('');
                    setContent('');
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  New Set
                </button>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  📥 Download
                </button>
              </div>
            </div>

            {/* Flashcard */}
            <div className="max-w-2xl mx-auto">
              <div 
                onClick={handleFlip}
                className="relative bg-white rounded-xl shadow-2xl cursor-pointer transition-transform duration-300 hover:scale-105"
                style={{ minHeight: '400px', perspective: '1000px' }}
              >
                <div className={`absolute inset-0 rounded-xl transition-transform duration-500 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                  {/* Front */}
                  <div className={`absolute inset-0 backface-hidden flex flex-col items-center justify-center p-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl ${isFlipped ? 'hidden' : ''}`}>
                    <div className="text-xs text-white/80 mb-4 font-medium uppercase tracking-wide">
                      Question
                    </div>
                    <div className="text-white text-xl sm:text-2xl font-bold text-center">
                      {currentCard?.front}
                    </div>
                    <div className="mt-8 text-white/60 text-sm">
                      Click to flip
                    </div>
                  </div>

                  {/* Back */}
                  <div className={`absolute inset-0 backface-hidden flex flex-col items-center justify-center p-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl ${!isFlipped ? 'hidden' : ''}`}>
                    <div className="text-xs text-white/80 mb-4 font-medium uppercase tracking-wide">
                      Answer
                    </div>
                    <div className="text-white text-lg sm:text-xl text-center whitespace-pre-wrap">
                      {currentCard?.back}
                    </div>
                    <div className="mt-8 text-white/60 text-sm">
                      Click to flip
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={handlePrevious}
                  disabled={currentCardIndex === 0}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  ← Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentCardIndex === flashcards.length - 1}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-100 disabled:text-gray-400 text-white font-semibold rounded-lg transition-colors"
                >
                  Next →
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentCardIndex + 1) / flashcards.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AIFlashcardGenerator;
